import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  Typography,
  Avatar,
  Chip,
  Tab,
  Tabs,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Breadcrumbs,
  Link,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Person,
  Work,
  Phone,
  Email,
  Event,
  School,
  Badge,
  Home,
  WorkOutline,
  Business,
  EventAvailable,
  AccountBalance,
  Security,
  PersonPin,
} from '@mui/icons-material';
// Remove the Page import as it's not needed
import { useEmployees } from '../../context';
import { Employee, EMPLOYEE_TYPES, EMPLOYEE_STATUSES } from '../../types/employee.types';
import EmployeeForm from '../../components/EmployeeForm';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`employee-tabpanel-${index}`}
      aria-labelledby={`employee-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `employee-tab-${index}`,
    'aria-controls': `employee-tabpanel-${index}`,
  };
}

const EmployeeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { state, fetchEmployeeById, updateEmployeeStatus } = useEmployees();
  
  const isNew = id === 'new';
  const isEdit = !!id && id !== 'new' && window.location.pathname.endsWith('/edit');
  const [tabValue, setTabValue] = useState(0);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const employee = isNew ? null : state.selectedEmployee;
  const loading = state.loading;

  useEffect(() => {
    const loadEmployee = async () => {
      if (id && id !== 'new' && !window.location.pathname.endsWith('/edit')) {
        try {
          await fetchEmployeeById(id);
        } catch (error) {
          enqueueSnackbar('Failed to load employee details', { variant: 'error' });
          navigate('/settings/employees');
        }
      }
    };
    loadEmployee();
    // We only need to refetch when id changes. Other deps are stable or cause loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // If editing or adding, show EmployeeForm
  if (isNew || isEdit) {
    return <EmployeeForm key={id || 'new'} />;
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleStatusChange = async (newStatus: 'ACTIVE' | 'INACTIVE' | 'ON_LEAVE' | 'TERMINATED') => {
    if (isNew) return; // Don't allow status change for new employees
    if (!employee) return;
    
    if (window.confirm(`Are you sure you want to mark this employee as ${newStatus.toLowerCase()}?`)) {
      try {
        setIsUpdatingStatus(true);
        await updateEmployeeStatus(employee?.id, newStatus);
        enqueueSnackbar(`Employee status updated to ${newStatus}`, { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Failed to update employee status', { variant: 'error' });
      } finally {
        setIsUpdatingStatus(false);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'default';
      case 'ON_LEAVE':
        return 'warning';
      case 'TERMINATED':
        return 'error';
      default:
        return 'default';
    }
  };

  if (state.loading && !isNew) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (!isNew && !employee && !state.loading) {
    return (
      <Container>
        <Box textAlign="center" py={5}>
          <Typography variant="h6" color="textSecondary">
            Employee not found
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/settings/employees')}
            sx={{ mt: 2 }}
          >
            Back to Employees
          </Button>
        </Box>
      </Container>
    );
  }
  
  // If editing or adding, show EmployeeForm
  if (isNew || isEdit) {
    return <EmployeeForm key={id || 'new'} />;
  }

  // For existing employee details, define currentEmployee
  const currentEmployee: Employee = employee || {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    employeeType: 'STAFF',
    status: 'ACTIVE',
    employeeId: '',
    userId: null,
    departmentId: null,
    designation: '',
    joiningDate: null,
    qualification: null,
    experience: null,
    emergencyContact: null,
    createdAt: '',
    updatedAt: '',
    department: null,
    user: null
  };

  return (
    <Box title={`${currentEmployee.firstName || 'Employee'} ${currentEmployee.lastName || ''} | Employee`}>
      <Container maxWidth={false}>
        <Box mb={3}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ mb: 2 }}
          >
            Back
          </Button>

          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link component={RouterLink} to="/settings/employees" color="inherit">
              Employees
            </Link>
            <Typography color="textPrimary">
              {currentEmployee.firstName} {currentEmployee.lastName}
            </Typography>
          </Breadcrumbs>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center">
              <Avatar
                src={currentEmployee.avatar}
                sx={{ width: 80, height: 80, mr: 3 }}
              >
                {currentEmployee.firstName?.[0]}
                {currentEmployee.lastName?.[0]}
              </Avatar>
              <Box>
                <Typography variant="h4" gutterBottom>
                  {currentEmployee.firstName} {currentEmployee.lastName}
                  <Chip
                    label={currentEmployee.status?.replace('_', ' ') || 'N/A'}
                    color={getStatusColor(currentEmployee.status || 'ACTIVE') as any}
                    size="small"
                    sx={{ ml: 2, textTransform: 'capitalize' }}
                  />
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  {EMPLOYEE_TYPES.find(t => t.value === currentEmployee.employeeType)?.label || currentEmployee.employeeType || 'N/A'}
                  {currentEmployee.department?.name && ` • ${currentEmployee.department.name}`}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Employee ID: {currentEmployee.employeeId || 'N/A'}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => {
                  if (currentEmployee.id) {
                    navigate(`/settings/employees/${currentEmployee.id}/edit`);
                  } else {
                    // Handle edit for new employee if needed
                  }
                }}
                sx={{ mr: 1 }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={isUpdatingStatus}
              >
                {isUpdatingStatus ? 'Updating...' : 'Actions'}
              </Button>
            </Box>
          </Box>
        </Box>

        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="employee details tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Overview" icon={<Person />} iconPosition="start" {...a11yProps(0)} />
              <Tab label="Employment" icon={<Work />} iconPosition="start" {...a11yProps(1)} />
              <Tab label="Documents" icon={<Badge />} iconPosition="start" {...a11yProps(2)} />
              <Tab label="Activity" icon={<Event />} iconPosition="start" {...a11yProps(3)} />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardHeader
                    title="Personal Information"
                    titleTypographyProps={{ variant: 'h6' }}
                  />
                  <Divider />
                  <CardContent>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <Email color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Email"
                          secondary={currentEmployee.email || 'N/A'}
                        />
                      </ListItem>
                      <Divider component="li" variant="inset" />
                      <ListItem>
                        <ListItemIcon>
                          <Phone color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Phone"
                          secondary={currentEmployee.phone || 'N/A'}
                        />
                      </ListItem>
                      <Divider component="li" variant="inset" />
                      <ListItem>
                        <ListItemIcon>
                          <Event color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Date of Birth"
                          secondary="N/A"
                        />
                      </ListItem>
                      <Divider component="li" variant="inset" />
                      <ListItem>
                        <ListItemIcon>
                          <PersonPin color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Gender"
                          secondary="N/A"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardHeader
                    title="Emergency Contact"
                    titleTypographyProps={{ variant: 'h6' }}
                  />
                  <Divider />
                  <CardContent>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Contact Person"
                          secondary={currentEmployee.emergencyContact || 'N/A'}
                        />
                      </ListItem>
                      <Divider component="li" variant="inset" />
                      <ListItem>
                        <ListItemText
                          primary="Relationship"
                          secondary="N/A"
                        />
                      </ListItem>
                      <Divider component="li" variant="inset" />
                      <ListItem>
                        <ListItemText
                          primary="Contact Number"
                          secondary="N/A"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader
                    title="Employment Details"
                    titleTypographyProps={{ variant: 'h6' }}
                  />
                  <Divider />
                  <CardContent>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <WorkOutline color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Designation"
                          secondary={currentEmployee.designation || 'N/A'}
                        />
                      </ListItem>
                      <Divider component="li" variant="inset" />
                      <ListItem>
                        <ListItemIcon>
                          <Business color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Department"
                          secondary={currentEmployee.department?.name || 'N/A'}
                        />
                      </ListItem>
                      <Divider component="li" variant="inset" />
                      <ListItem>
                        <ListItemIcon>
                          <EventAvailable color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Joining Date"
                          secondary={currentEmployee.joiningDate 
                            ? new Date(currentEmployee.joiningDate).toLocaleDateString() 
                            : 'N/A'}
                        />
                      </ListItem>
                      <Divider component="li" variant="inset" />
                      <ListItem>
                        <ListItemIcon>
                          <AccountBalance color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Experience"
                          secondary={currentEmployee.experience ? `${currentEmployee.experience} years` : 'N/A'}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader
                    title="Education & Skills"
                    titleTypographyProps={{ variant: 'h6' }}
                  />
                  <Divider />
                  <CardContent>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <School color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Qualification"
                          secondary={currentEmployee.qualification || 'N/A'}
                        />
                      </ListItem>
                      <Divider component="li" variant="inset" />
                      <ListItem>
                        <ListItemText
                          primary="Skills"
                          secondary="N/A"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>

                <Box mt={3}>
                  <Card variant="outlined">
                    <CardHeader
                      title="Account Status"
                      titleTypographyProps={{ variant: 'h6' }}
                    />
                    <Divider />
                    <CardContent>
                      <List>
                        <ListItem>
                          <ListItemText
                            primary="Status"
                            secondary={
                              <Box>
                                <Chip
                                  label={employee?.status?.replace('_', ' ')}
                                  color={getStatusColor(employee?.status) as any}
                                  size="small"
                                  sx={{ textTransform: 'capitalize' }}
                                />
                                <Box mt={1}>
                                  <Typography variant="body2" color="textSecondary">
                                    Change status to:
                                  </Typography>
                                  <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                                    {employee && EMPLOYEE_STATUSES.map((status) => (
                                      status.value !== employee?.status && (
                                        <Chip
                                          key={status.value}
                                          label={status.label}
                                          variant="outlined"
                                          onClick={() => handleStatusChange(status.value as 'ACTIVE' | 'INACTIVE' | 'ON_LEAVE' | 'TERMINATED')}
                                          sx={{ cursor: 'pointer' }}
                                          disabled={isUpdatingStatus}
                                        />
                                      )
                                    ))}
                                  </Box>
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                No documents uploaded yet
              </Typography>
              <Typography color="textSecondary" paragraph>
                Upload documents such as certifications, contracts, and other important files.
              </Typography>
              <Button variant="contained" color="primary">
                Upload Document
              </Button>
            </Paper>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Activity Log
              </Typography>
              <Typography color="textSecondary">
                No recent activity to display.
              </Typography>
            </Paper>
          </TabPanel>
        </Card>
      </Container>
    </Box>
  );
};

export default EmployeeDetailPage;
